/**
 * Personal Loan Cross Sell Insert Service
 * Migrated from 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/plcsinsert.ts
 * Logic preserved AS-IS, updated to use local monorepo patterns
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import type { IPlcsInsertApiRequest, IPlcsInsertApiResponse } from "../types/plcsInsertApiTypes";
import { getLoginState, commonInsertParameters } from "@repo/shared-utils/insert-params";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { request } from "../core/request";
import { deleteFromCartApi } from "./cart.service";
import {
  API_RESPONSE_CODES,
  LOAN_TYPES_MAPPING,
  OPERATION_IDS,
  PLCS_GIN,
  PLCS_LOAN_VARIANT,
  V5,
} from "./constants";
import {
  detectAndRegisterNetworkError,
  getM1TrackingParams,
  getSalPLadditionalPayload,
  removeM1Fields,
  setUtmParamsForInsertConnectionManager,
} from "./insertUtils";

async function deleteFromCart(mobileNo: string, appId: string) {
  try {
    await deleteFromCartApi({ mobileNo, appId, channel: "3in1" });
  } catch (deleteError) {
    logPageActionToNewRelic("PLCS_DELETE_CART_ERROR", {
      component: "PLCS Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (deleteError as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!mobileNo,
      }),
    });
  }
}

export const plcsInsert = async (data: IPlcsInsertApiRequest): Promise<IPlcsInsertApiResponse> => {
  try {
    if (data.mobileNo && data.appId) {
      await deleteFromCart(data.mobileNo, data.appId);
    }

    if (data.loanVariant === "hybrid") {
      const dropline = typeof data.droplineEMIAmt === "string" ? data.droplineEMIAmt : undefined;
      const hybrid = typeof data.hybridEMIAmt === "string" ? data.hybridEMIAmt : undefined;
      data.emiAmt = dropline ?? hybrid;
    }

    if (data.loanVariant) {
      data.loanVariant = LOAN_TYPES_MAPPING[data.loanVariant] ?? data.loanVariant;
    } else {
      data.loanVariant = PLCS_LOAN_VARIANT;
    }

    const modifiedData = { ...data, ...getM1TrackingParams() };
    const additionalDetails = getSalPLadditionalPayload(modifiedData, PLCS_GIN);
    const filteredData = removeM1Fields(modifiedData);

    const finalPayload = {
      ...filteredData,
      ...additionalDetails,
      ...(await setUtmParamsForInsertConnectionManager()),
      saveForLaterFlag: true,
      ...(modifiedData.startNewApplication && { id: "" }),
    };

    const response = (await request({
      operationId: OPERATION_IDS.plcsInsert,
      requestData: {
        ...commonInsertParameters(),
        appVersion: V5,
        ...finalPayload,
      },
    })) as IPlcsInsertApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    detectAndRegisterNetworkError(error as Error, "plcsInsert");

    logPageActionToNewRelic("PLCS_INSERT_ERROR", {
      component: "PLCS Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });

    throw error;
  }
};
